import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useUnmount } from 'react-use';
import opConf from 'openpathConfig';

import { checkIsVideoOnlyCustomer } from 'utils/checkIsVideoOnlyCustomer';
import { checkAuthIsUPCPartnerOrg } from 'utils/checkAuthIsUPCPartnerOrg';
import { FormOnSubmitType, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { checkMasterModeAccess } from 'new-components/OrgSwitcher/helpers/orgAccess';
import { getAppOrigin } from 'utils/getAppOrigin';
import { getControlCenterOrigin } from 'utils/getControlCenterOrigin';
import { mfaManagementRoute } from 'routes/constants';
import { setWindowLocation } from 'utils/setWindowLocation';
import { getWindowLocation } from 'utils/window';

import { hasOnlyMailroomScopes } from '../../helpers/hasOnlyMailroomScopes';
import { isValidAbsoluteRedirectUrl } from '../../helpers/isValidAbsoluteRedirectUrl';
import { setAltaCookie } from '../../helpers/setAltaCookie';
import { Logins } from '../../helpers/getPossibleLogins';
import { getOrgIdForRedirect } from '../../helpers/getOrgIdForRedirect';
import { NotYou } from '../NotYou/NotYou';
import { useAltaLogin } from '../../hooks/useAltaLogin';
import { redirectToAccess } from '../../helpers/redirectToAccess';
import { ErrorAlert } from '../ErrorAlert/ErrorAlert';
import { useAltaLoginUrlParams } from '../../hooks/useAltaLoginUrlParams';
import { redirectToUpc } from '../../helpers/redirectToUpc';
import { getVideoOnlyCustomerRedirectUrl } from '../../helpers/getVideoOnlyCustomerRedirectUrl';
import { getUpcUrl } from '../../helpers/getUpcUrl';
import { redirectToMailroom } from '../../helpers/redirectToMailroom';
import { isVideoOnlyUser } from '../../helpers/isVideoOnlyUser';

import { removeTrailingUrlSlash } from './utils/removeTrailingUrlSlash';

import './PasswordForm.scss';

type Login = Utils.DeepNonUndefined<Api.Response['login']>;

interface PasswordFormProps {
  email: string;
  namespace: Logins['namespaces'][0];
  regionEnv: OP.Env;
  heliumEndpoint: string;
  resetLogins: () => void;
}

/**
 * @component
 * @description Component that allows the user enter their password.
 * @param {string} props.email - email they are using to login with.
 * @param {string} props.namespace - the namespace the user chose to login with.
 * @param {string} props.regionEnv - region env name the user is logging into.
 * @param {string} props.heliumEndpoint - endpoint for the current environment and chosen region to call /auth endpoints.
 * @param {string} props.resetLogins - callback to reset the possible logins and the user's email, takes you back to emailForm
 * @returns {JSX.Element} React component
 */
export const PasswordForm = ({
  email,
  namespace,
  regionEnv,
  heliumEndpoint,
  resetLogins,
}: PasswordFormProps) => {
  // Use override for redirect target in local development/e2e tests
  const region =
    opConf.LOGIN_REDIRECT_REGION_OVERRIDE || regionEnv || opConf.ENV;
  const { t } = useTranslation();
  const { orgId, redirectUrl, service, nonce } = useAltaLoginUrlParams();
  const [isShowing2fa, setIsShowing2fa] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const redirectOrgId = getOrgIdForRedirect(
    orgId,
    namespace,
    service === 'dmp',
  );

  const isValidAbsUrl = isValidAbsoluteRedirectUrl(redirectUrl);

  const redirect_url = isValidAbsUrl ? decodeURIComponent(redirectUrl) : null;

  const options = {
    region: regionEnv,
    org_id: Number(redirectOrgId),
    nonce,
    ...(redirect_url ? { redirect_url } : {}),
  };

  const { handleAltaLogin, isLoggingIn } = useAltaLogin({
    url: isValidAbsUrl ? new URL(redirectUrl).origin : '',
    urlToOpen: redirectUrl,
    options,
    setError,
  });

  /**
   * Hack for now...we need to launch and we need to rethink the handleAltaLogin function.
   * When we have shared domains  and cookies we shouldn't need this at all
   */
  const isRedirectUrlUPC = redirectUrl?.includes(
    removeTrailingUrlSlash(new URL(getUpcUrl(region)).href),
  );

  const isRedirectUrlMailroom = redirectUrl?.includes(getAppOrigin('mailroom'));

  const isRedirectUrlSOS = redirectUrl?.includes(getAppOrigin('sos'));

  const isMfaError = (loginError: OP.ApiError) => {
    return (
      loginError.statusCode === 401 &&
      loginError.message === 'Multi-factor authentication is required.'
    );
  };

  const onSubmit: FormOnSubmitType = async ({
    values: { password, totpCode },
  }) => {
    setError(undefined);
    setIsSubmitting(true);
    const loginResponse = await fetch(`${heliumEndpoint}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        namespaceId: namespace.id,
        ...(totpCode && { mfa: { totpCode } }),
      }),
    });
    const data = (await loginResponse.json()) as { data: Login } & OP.ApiError;
    setIsSubmitting(false);

    if (loginResponse.status === 201) {
      // Set cookie based on the actual environment of the identity

      setAltaCookie(data.data.token, regionEnv);

      const tokenScopeList = data.data?.tokenScopeList ?? [];

      const tokenScopeListItem = tokenScopeList.find((scopeListItem) => {
        return scopeListItem.org.id === Number(redirectOrgId);
      });

      const hasNeedsMfaCredential = (
        scopeListItem?: Login['tokenScopeList'][number],
      ) => {
        if (!scopeListItem) return false;
        const hasNeedsMfaCredentialOnUser = Boolean(
          scopeListItem.user?.needsMFACredential,
        );
        const hasNeedsMfaCredentialInContext = Boolean(
          scopeListItem.scopeWithContext?.some((scope) =>
            scope.context?.some((context) => context.needsMFACredential),
          ),
        );
        return hasNeedsMfaCredentialOnUser || hasNeedsMfaCredentialInContext;
      };

      const org = tokenScopeListItem?.org;
      const needsMFACredential = hasNeedsMfaCredential(tokenScopeListItem);
      const { search } = getWindowLocation();

      if (needsMFACredential && !totpCode) {
        const ccDomain = getControlCenterOrigin();
        setWindowLocation(`${ccDomain}${mfaManagementRoute}${search ?? ''}`);
        return;
      }

      if (checkMasterModeAccess({ token: data.data })) {
        redirectToAccess(region, 'master');
      } else if (isValidAbsoluteRedirectUrl(redirectUrl)) {
        // redirectUrl is checked before app-specific heuristics (partner, video, mailroom)
        // so the user returns to their intended destination after login. See NEX-970.
        handleAltaLogin({
          bypassAuth:
            isRedirectUrlUPC || isRedirectUrlMailroom || isRedirectUrlSOS,
        });
      } else if (checkAuthIsUPCPartnerOrg(tokenScopeListItem)) {
        const queryParams = service === 'dmp' ? { service: 'dmp' } : null;
        redirectToUpc(region, org?.id!, queryParams);
      } else if (
        checkIsVideoOnlyCustomer(org?.orgNativeApps) ||
        isVideoOnlyUser(tokenScopeListItem)
      ) {
        const videoUrl = getVideoOnlyCustomerRedirectUrl(org!);
        handleAltaLogin({
          url: videoUrl!,
          urlToOpen: videoUrl!, // ew
        });
      } else if (hasOnlyMailroomScopes(tokenScopeListItem)) {
        redirectToMailroom(region, tokenScopeListItem?.org?.id!);
      } else {
        // go to control center when no redirectUrl passed in
        redirectToAccess(region);
      }
    } else if (isMfaError(data)) {
      setIsShowing2fa(true);
    } else {
      setError(data.message);
    }
  };

  useUnmount(() => {
    setError(undefined);
  });

  const { org, identityProvider } = namespace;

  const name =
    !org && !identityProvider
      ? namespace.nickname
      : org?.name || identityProvider?.org?.name;

  return (
    <div>
      <NotYou email={email} name={name} onResetClick={resetLogins} />
      {error && <ErrorAlert message={error} />}
      <OpForm
        className="password-form"
        defaultButtons={false}
        onSubmit={onSubmit}
        autoComplete="off"
        shouldWarnChangesBeforeUnload={false}
        hasError={false}
      >
        <OpForm.PasswordInput
          className="password-form__password"
          name="password"
          label={t('Password')}
          validateTrigger="onSubmit"
          rules={[
            {
              required: true,
              message: t('Password is required'),
            },
          ]}
        />

        {isShowing2fa && (
          <OpForm.Input name="totpCode" label={t('2FA code')} autoFocus />
        )}

        {!isShowing2fa && (
          <OpForm.TextButton
            onClick={() => {
              setIsShowing2fa(true);
            }}
          >
            {t('Enter two-factor authentication code')}
          </OpForm.TextButton>
        )}

        <OpForm.Button
          block
          className="password-form__sign-in-button"
          htmlType="submit"
          type="primary"
          loading={isSubmitting || isLoggingIn}
        >
          {t('Sign in')}
        </OpForm.Button>
      </OpForm>
    </div>
  );
};
